import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import articleBg from "@/assets/article-bg.jpg";

interface TermsSection {
  id: string;
  title: string;
  content: string;
}

const termsSections: TermsSection[] = [
  {
    id: "about-animind",
    title: "1. About Animind",
    content: `Animind provides AI-assisted film, video, image, animation, story-world generation, character consistency, scene design, shot refinement, and related creative production tools.

Animind is a digital, cloud-based, business-to-business platform. Our services may include access to AI generation tools, project workflows, usage credits, points, memberships, enterprise features, API access, onboarding support, technical assistance, or other digital production services.`,
  },
  {
    id: "business-use-only",
    title: "2. Business Use Only",
    content: `Animind is intended for business and professional users only. You represent and warrant that you are using Animind for business, commercial, professional, or organizational purposes, and not as a consumer or for personal household use.

We may require business verification before approving your account. Verification may include company name, business email, company website, business license, corporate registration documents, tax information, billing information, or other materials reasonably required to confirm your business identity.

We reserve the right to approve, reject, suspend, or terminate access at our discretion if we cannot verify your business identity or if we believe your application, account, or usage creates legal, compliance, payment, security, or reputational risk.`,
  },
  {
    id: "account-registration",
    title: "3. Account Registration and Security",
    content: `To use certain services, you must apply for and receive an approved account. You agree to provide accurate, current, and complete information during registration, verification, billing, and ongoing account use.

You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You must promptly notify us if you suspect unauthorized access, credential compromise, or security breach.

You may not share, resell, lease, sublicense, transfer, or provide unauthorized access to your account without our prior written consent.

If you create or manage an account on behalf of a company or organization, you represent that you have authority to bind that company or organization to these Terms.`,
  },
  {
    id: "access-approval",
    title: "4. Access Approval",
    content: `Submitting an application or payment does not guarantee account approval. Animind may review applications manually or automatically and may request additional information before granting access.

If payment has been made but access is not approved due to an issue caused by Animind, your remedy will be handled according to our Refund Policy.

We may deny access to users, businesses, regions, industries, or use cases that we determine to be unsupported, high risk, unlawful, abusive, or inconsistent with our business policies.`,
  },
  {
    id: "customer-content",
    title: "5. Customer Content",
    content: `"Customer Content" means any content, data, materials, files, prompts, scripts, storyboards, character descriptions, visual references, images, videos, audio, project files, creative instructions, metadata, or other materials that you upload, submit, generate, store, process, or transmit through Animind.

You retain ownership of your Customer Content, subject to the rights and licenses granted in these Terms and any separate written agreement.

You grant ATELIER TENET INC. a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, transmit, display, modify, and use Customer Content solely as necessary to provide, maintain, secure, troubleshoot, improve, and support Animind and related services.

You represent and warrant that you have all rights, licenses, permissions, and legal authority required to upload, submit, process, or generate Customer Content through Animind.`,
  },
  {
    id: "ai-outputs",
    title: "6. AI Outputs",
    content: `"Outputs" means images, videos, animations, clips, edits, concepts, designs, scene materials, character materials, or other content generated or modified through Animind.

Subject to your compliance with these Terms, our Refund Policy, payment obligations, third-party model terms, and applicable law, Animind does not claim ownership over Outputs generated from your valid Customer Content.

However, you understand and agree that AI-generated outputs may not be unique. Similar or identical outputs may be generated for other users due to the nature of AI systems, model behavior, prompts, public domain concepts, common styles, or similar input instructions.

You are solely responsible for reviewing Outputs before commercial use, including for legal clearance, copyright, trademark, likeness rights, defamation, privacy, advertising compliance, industry standards, and platform distribution requirements.

Animind does not guarantee that any Output is copyrightable, protectable, registrable, exclusive, non-infringing, commercially suitable, error-free, or accepted by any distributor, platform, broadcaster, advertiser, regulator, or marketplace.`,
  },
  {
    id: "third-party-models",
    title: "7. Use of Third-Party AI Models and Service Providers",
    content: `Animind may integrate, route, process, or support services through third-party AI models, APIs, cloud platforms, infrastructure providers, storage providers, security providers, payment processors, analytics providers, and other service providers.

Your use of certain features may be subject to third-party terms, policies, technical limitations, content restrictions, usage caps, service availability, and processing rules.

We are not responsible for downtime, errors, refusals, moderation decisions, data processing limitations, model behavior, output quality, API changes, pricing changes, policy changes, or service interruptions caused by third-party providers.`,
  },
  {
    id: "no-guaranteed-results",
    title: "8. No Guaranteed Results",
    content: `Animind provides AI-assisted creative tools. We do not guarantee that the services will produce any specific creative, commercial, technical, financial, aesthetic, legal, or distribution result.

AI outputs may contain errors, inconsistencies, artifacts, distortions, unexpected details, inaccurate interpretations, or unsuitable elements. You are responsible for reviewing, editing, validating, and approving all Outputs before use.`,
  },
  {
    id: "payments-credits",
    title: "9. Payments, Credits, and Points",
    content: `Animind may offer paid memberships, account access, usage packages, credits, points, tokens, generation balances, enterprise plans, professional services, or other paid services.

All fees must be paid in full according to the pricing, invoice, checkout page, order form, or written agreement applicable to your purchase.

Credits, points, tokens, or usage balances are digital service entitlements only. They are not bank deposits, stored-value products, gift cards, securities, currency, or cash equivalents. They have no cash value and cannot be redeemed for cash unless required by law.

We may modify pricing, packages, plan features, usage limits, or credit consumption rules from time to time. Changes will not retroactively alter completed purchases unless required by law or expressly stated.

Refunds are governed by our Refund Policy.`,
  },
  {
    id: "taxes",
    title: "10. Taxes",
    content: `Fees are exclusive of applicable taxes unless otherwise stated. You are responsible for all taxes, duties, levies, withholding, or governmental charges arising from your purchase or use of Animind, except taxes based on our net income.

We may collect tax information or charge applicable taxes where required by law.`,
  },
  {
    id: "acceptable-use",
    title: "11. Acceptable Use",
    content: `You agree not to use Animind for any unlawful, harmful, deceptive, abusive, infringing, fraudulent, or high-risk purpose.

You must not use Animind to create, upload, process, distribute, or assist with content that:

Violates any law, regulation, court order, sanctions rule, export control rule, or third-party right.

Infringes copyright, trademark, trade secret, privacy rights, publicity rights, likeness rights, contractual rights, or other intellectual property rights.

Contains or promotes child sexual abuse material, sexual exploitation of minors, grooming, or any content involving minors in a sexual context.

Promotes terrorism, violent extremism, organized crime, human trafficking, exploitation, or serious physical harm.

Creates non-consensual sexual content, intimate imagery, deepfake sexual content, or sexualized likenesses of real persons without consent.

Impersonates a real person, company, public authority, or brand in a deceptive or harmful manner.

Creates deceptive political, financial, medical, legal, or public-interest content without appropriate disclosure and compliance.

Contains malware, phishing content, credential theft, spyware, ransomware, unauthorized surveillance, or instructions for cyber abuse.

Facilitates fraud, scams, identity theft, payment abuse, chargeback abuse, or document falsification.

Generates defamatory, harassing, hateful, threatening, or discriminatory content.

Attempts to bypass safety systems, moderation systems, usage limits, billing systems, access controls, or security controls.

We may remove content, restrict features, suspend accounts, terminate services, or report activity to authorities where we believe there is a violation of these Terms or applicable law.`,
  },
  {
    id: "prohibited-technical",
    title: "12. Prohibited Technical Conduct",
    content: `You must not:

Reverse engineer, decompile, disassemble, scrape, crawl, copy, or attempt to extract source code, models, prompts, system instructions, weights, training data, architecture, or non-public technical information.

Interfere with, overload, disrupt, or damage Animind, its infrastructure, networks, security systems, APIs, or service providers.

Use bots, scripts, automated tools, or unauthorized integrations without our written permission.

Circumvent rate limits, usage caps, payment systems, geographic restrictions, compliance checks, or access controls.

Use Animind to develop, benchmark, or train a competing service without our written consent.

Misrepresent usage, business identity, payment identity, ownership, authorization, or source of funds.`,
  },
  {
    id: "ip-animind",
    title: "13. Intellectual Property of Animind",
    content: `Animind, including its platform, software, workflows, interface design, technology, systems, algorithms, documentation, branding, logos, trademarks, trade names, design elements, and service features, is owned by ATELIER TENET INC. or its licensors.

Except for the limited right to use the services under these Terms, no rights are granted to you in Animind's intellectual property.

You may not copy, modify, distribute, sell, lease, sublicense, create derivative works from, or exploit any part of Animind without our prior written consent.`,
  },
  {
    id: "feedback",
    title: "14. Feedback",
    content: `If you provide feedback, suggestions, ideas, requests, comments, or recommendations about Animind, you grant ATELIER TENET INC. a perpetual, irrevocable, worldwide, royalty-free license to use, modify, implement, commercialize, and incorporate that feedback without compensation or obligation to you.`,
  },
  {
    id: "confidentiality",
    title: "15. Confidentiality",
    content: `During your use of Animind, you may receive access to non-public information about our platform, features, pricing, technology, roadmap, business plans, partners, or services. You agree not to disclose such confidential information to any third party without our prior written consent.

We will treat your confidential Customer Content according to these Terms, our Privacy Policy, and any separate written agreement.`,
  },
  {
    id: "privacy",
    title: "16. Privacy",
    content: `Our collection, use, storage, and disclosure of personal information are described in our Privacy Policy. By using Animind, you acknowledge our Privacy Policy.

You are responsible for ensuring that any personal information you upload or process through Animind is collected and submitted in compliance with applicable privacy, data protection, and consent laws.`,
  },
  {
    id: "service-availability",
    title: "17. Service Availability and Changes",
    content: `We may modify, suspend, discontinue, limit, replace, or update any part of Animind at any time, including features, models, tools, pricing, plans, credits, access requirements, storage limits, usage limits, or technical capabilities.

We do not guarantee uninterrupted, secure, or error-free service. Maintenance, third-party downtime, security incidents, model changes, API interruptions, network issues, compliance reviews, or force majeure events may affect service availability.`,
  },
  {
    id: "suspension-termination",
    title: "18. Suspension and Termination",
    content: `We may suspend, restrict, or terminate your account or access to Animind if:

You violate these Terms, our Refund Policy, our Privacy Policy, or any applicable agreement.

Your payment fails, is reversed, disputed, charged back, suspected to be fraudulent, or otherwise creates payment risk.

Your account, content, business, region, industry, or use case creates legal, compliance, security, reputational, sanctions, fraud, or operational risk.

You provide false, incomplete, misleading, or unverifiable information.

We are required to do so by law, court order, regulator, payment processor, financial institution, service provider, or law enforcement authority.

Upon termination, your right to use Animind will cease. We may delete, retain, or restrict access to account data and Customer Content according to our Privacy Policy, legal obligations, security needs, and operational policies.`,
  },
  {
    id: "disclaimers",
    title: "19. Disclaimers",
    content: `Animind is provided on an "as is" and "as available" basis.

To the maximum extent permitted by law, ATELIER TENET INC. disclaims all warranties, whether express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, availability, reliability, compatibility, security, and uninterrupted operation.

We do not warrant that Animind, third-party models, Outputs, or any related services will meet your requirements, be error-free, be legally compliant for your intended use, or produce any particular result.`,
  },
  {
    id: "limitation-liability",
    title: "20. Limitation of Liability",
    content: `To the maximum extent permitted by law, ATELIER TENET INC., its directors, officers, employees, contractors, affiliates, licensors, service providers, and partners will not be liable for indirect, incidental, consequential, special, exemplary, punitive, or lost-profit damages, including loss of revenue, data, goodwill, business opportunity, production cost, distribution opportunity, or commercial value.

To the maximum extent permitted by law, our total aggregate liability arising out of or relating to Animind, these Terms, or any services will not exceed the amount you paid to ATELIER TENET INC. for the services giving rise to the claim during the three months immediately preceding the event giving rise to liability.

Some jurisdictions do not allow certain limitations of liability. In such cases, liability will be limited to the maximum extent permitted by law.`,
  },
  {
    id: "indemnification",
    title: "21. Indemnification",
    content: `You agree to defend, indemnify, and hold harmless ATELIER TENET INC., its directors, officers, employees, contractors, affiliates, licensors, service providers, and partners from and against any claims, damages, liabilities, losses, costs, expenses, and fees, including reasonable legal fees, arising from or related to:

Your use of Animind.

Your Customer Content or Outputs.

Your violation of these Terms.

Your violation of applicable law or third-party rights.

Your payment disputes, chargebacks, fraud, misrepresentation, or unauthorized account activity.

Your commercial use, distribution, publication, sale, licensing, or exploitation of any Output.`,
  },
  {
    id: "export-sanctions",
    title: "22. Export Control and Sanctions Compliance",
    content: `You agree not to use Animind in violation of applicable export control, sanctions, anti-money laundering, anti-bribery, anti-corruption, or trade compliance laws.

You represent that you are not located in, organized under the laws of, or ordinarily resident in any jurisdiction subject to comprehensive sanctions, and that you are not listed on any applicable sanctions or restricted-party list.

We may restrict or terminate access if we believe your account or use creates sanctions, export control, or financial crime compliance risk.`,
  },
  {
    id: "governing-law",
    title: "23. Governing Law",
    content: `These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles.`,
  },
  {
    id: "dispute-resolution",
    title: "24. Dispute Resolution",
    content: `Before bringing a formal claim, you agree to contact us at support@animind.net and attempt to resolve the dispute informally.

If the dispute cannot be resolved informally, the courts located in Ontario, Canada will have exclusive jurisdiction, unless applicable law requires otherwise.

You agree that any claim must be brought individually and not as part of a class, collective, consolidated, or representative action, to the maximum extent permitted by law.`,
  },
  {
    id: "force-majeure",
    title: "25. Force Majeure",
    content: `We will not be liable for any delay, failure, interruption, or loss caused by events beyond our reasonable control, including natural disasters, war, terrorism, civil unrest, labor disputes, pandemics, government actions, power failures, internet outages, cloud provider failures, third-party API failures, payment network failures, cyberattacks, or other force majeure events.`,
  },
  {
    id: "assignment",
    title: "26. Assignment",
    content: `You may not assign or transfer these Terms or your account without our prior written consent.

We may assign or transfer these Terms, in whole or in part, in connection with a merger, acquisition, financing, corporate restructuring, sale of assets, change of control, or operation of law.`,
  },
  {
    id: "changes",
    title: "27. Changes to These Terms",
    content: `We may update these Terms from time to time. The updated version will be posted on this page with a new effective date.

If changes are material, we may provide additional notice by email, account notice, website notice, or other reasonable means.

Your continued use of Animind after updated Terms become effective means that you accept the updated Terms.`,
  },
  {
    id: "entire-agreement",
    title: "28. Entire Agreement",
    content: `These Terms, together with our Privacy Policy, Refund Policy, applicable order forms, invoices, written agreements, and any additional terms referenced herein, constitute the entire agreement between you and ATELIER TENET INC. regarding Animind.

If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.`,
  },
  {
    id: "contact",
    title: "29. Contact Us",
    content: `For questions about these Terms, contact:

ATELIER TENET INC.
3660 Midland Ave, Suite 326
Scarborough, ON M1V 0B8
Canada
Email: support@animind.net
Website: https://www.animind.net`,
  },
];

const termsMeta = {
  effectiveDate: "[Month Day, Year]",
  website: "https://www.animind.net",
  company: "ATELIER TENET INC.",
  address: "3660 Midland Ave, Suite 326, Scarborough, ON M1V 0B8, Canada",
  contact: "support@animind.net",
};

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-gray-300 bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${articleBg})` }}
    >
      <div className="min-h-screen bg-black/80">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </motion.div>

          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            <div className="text-gray-400 space-y-1">
              <p>Effective Date: {termsMeta.effectiveDate}</p>
              <p>Website: {termsMeta.website}</p>
              <p>Company: {termsMeta.company}</p>
              <p>Address: {termsMeta.address}</p>
              <p>Contact: {termsMeta.contact}</p>
            </div>
          </motion.header>

          <div className="lg:grid lg:grid-cols-4 lg:gap-12">
            <motion.nav
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block lg:col-span-1"
            >
              <div className="sticky top-8">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Table of Contents
                </h2>
                <ul className="space-y-2">
                  {termsSections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className="text-sm text-gray-400 hover:text-orange-500 transition-colors duration-200 text-left"
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.nav>

            <motion.main
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-3"
            >
              <p className="text-gray-400 mb-8 leading-relaxed">
                These Terms of Service govern your access to and use of Animind,
                a business-to-business AI film and animation creation platform
                operated by ATELIER TENET INC.
              </p>
              <p className="text-gray-400 mb-12 leading-relaxed">
                By accessing our website, submitting an application, creating an
                account, purchasing services, uploading content, or using
                Animind, you agree to these Terms of Service. If you do not
                agree, you must not access or use Animind.
              </p>

              {termsSections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="mb-10 scroll-mt-8"
                >
                  <h2 className="text-xl font-semibold text-white mb-4">
                    {section.title}
                  </h2>
                  <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </section>
              ))}
            </motion.main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
